import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, X, Check, Trash2, BarChart3, Link2, Copy, CheckCheck, Mail, AlertTriangle, Flame, GitBranch, ChevronDown, ChevronRight, Zap } from 'lucide-react';
import PageShell from '../components/PageShell';
import { motion, AnimatePresence } from 'framer-motion';
import ProGate from '../components/ProGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


function copyToClipboard(text) {
  // Always use the textarea fallback — clipboard API is blocked by permissions policy in iframes/preview environments
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try { document.execCommand('copy'); } catch {}
  document.body.removeChild(textarea);
  return Promise.resolve();
}

function InviteForm({ onInviteSent, user }) {
  const [tab, setTab] = useState('username'); // 'username' | 'link'
  const [username, setUsername] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [copied, setCopied] = useState(false);

  const referralCode = user?.referral_code;
  const referralLink = referralCode ? `${window.location.origin}/r/${referralCode}` : '';

  const handleUsernameInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    setMsg(null);
    try {
      const res = await authFetch(`${API}/api/crew/invite-by-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username }),
      });
      const data = await res.json();
      if (res.ok) {
        setMsg({ type: 'ok', text: 'invite sent!' });
        setUsername('');
        onInviteSent();
      } else {
        setMsg({ type: 'err', text: data.detail || 'failed to send' });
      }
    } catch {
      setMsg({ type: 'err', text: 'network error' });
    } finally {
      setSending(false);
    }
  };

  const handleCopy = () => {
    copyToClipboard(referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mb-5" data-testid="crew-invite-form">
      <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Add to crew</p>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-3">
        <button
          data-testid="invite-tab-username"
          onClick={() => { setTab('username'); setMsg(null); }}
          className={`flex-1 text-xs font-medium py-2 rounded-lg transition-all inline-flex items-center justify-center gap-1.5
            ${tab === 'username' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <UserPlus className="w-3.5 h-3.5" />
          Username
        </button>
        <button
          data-testid="invite-tab-link"
          onClick={() => { setTab('link'); setMsg(null); }}
          className={`flex-1 text-xs font-medium py-2 rounded-lg transition-all inline-flex items-center justify-center gap-1.5
            ${tab === 'link' ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <Link2 className="w-3.5 h-3.5" />
          Referral Link
        </button>
      </div>

      {/* Username Tab */}
      {tab === 'username' && (
        <form onSubmit={handleUsernameInvite}>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-xs">@</span>
              <input
                data-testid="crew-invite-username"
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                placeholder="username"
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-7 pr-3 py-2.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
                required
              />
            </div>
            <button
              data-testid="crew-invite-btn"
              type="submit"
              disabled={sending}
              className="bg-primary text-primary-foreground text-xs font-bold px-4 py-2.5 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {sending ? '...' : 'Add'}
            </button>
          </div>
        </form>
      )}

      {/* Link Tab — permanent referral link */}
      {tab === 'link' && (
        <div>
          {referralCode ? (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <div className="flex gap-2">
                <div className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs text-muted-foreground truncate" data-testid="referral-link-display">
                  surfswell.ai/r/{referralCode}
                </div>
                <button
                  data-testid="copy-referral-link-btn"
                  onClick={handleCopy}
                  className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all inline-flex items-center gap-1.5 ${
                    copied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-primary text-primary-foreground hover:opacity-90'
                  }`}
                >
                  {copied ? <><CheckCheck className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/60">your permanent referral link — earns aura when friends join</p>
            </motion.div>
          ) : (
            <p className="text-xs text-muted-foreground/60 py-3 text-center">loading your referral link...</p>
          )}
        </div>
      )}

      {msg && (
        <p className={`text-[11px] mt-1.5 ${msg.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
          {msg.text}
        </p>
      )}
    </div>
  );
}

function PendingInvites({ invites, onAction }) {
  if (!invites.received?.length && !invites.sent?.length) return null;

  const handleAccept = async (id) => {
    await authFetch(`${API}/api/crew/invites/${id}/accept`, { method: 'POST',  });
    onAction();
  };
  const handleDecline = async (id) => {
    await authFetch(`${API}/api/crew/invites/${id}/decline`, { method: 'POST',  });
    onAction();
  };

  return (
    <div className="mb-5">
      {invites.received?.length > 0 && (
        <>
          <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Incoming</p>
          <div className="space-y-2 mb-4">
            {invites.received.map(inv => (
              <motion.div
                key={inv.invite_id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-primary/5 border border-primary/20 rounded-2xl p-3 flex items-center gap-3"
                data-testid={`invite-received-${inv.invite_id}`}
              >
                <div className="w-8 h-8 rounded-full overflow-hidden border border-white/10 flex-shrink-0">
                  {inv.from_picture ? (
                    <img src={inv.from_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center text-[10px] font-bold">
                      {(inv.from_name || '?')[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{inv.from_name || inv.from_username}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {inv.from_username ? `@${inv.from_username} ` : ''}wants to join your crew
                  </p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button
                    data-testid={`accept-invite-${inv.invite_id}`}
                    onClick={() => handleAccept(inv.invite_id)}
                    className="p-1.5 bg-green-500/20 hover:bg-green-500/30 rounded-full transition-colors"
                  >
                    <Check className="w-3.5 h-3.5 text-green-400" />
                  </button>
                  <button
                    data-testid={`decline-invite-${inv.invite_id}`}
                    onClick={() => handleDecline(inv.invite_id)}
                    className="p-1.5 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}
      {invites.sent?.length > 0 && (
        <>
          <p className="text-xs tracking-widest uppercase text-muted-foreground mb-2">Sent</p>
          <div className="space-y-2 mb-4">
            {invites.sent.map(inv => (
              <div key={inv.invite_id} className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-3 flex items-center gap-3" data-testid={`invite-sent-${inv.invite_id}`}>
                <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <p className="text-xs text-muted-foreground flex-1 truncate">{inv.to_email || 'via username'}</p>
                <span className="text-[10px] text-muted-foreground/60 bg-white/5 px-2 py-0.5 rounded-lg">pending</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CrewMemberCard({ member, onRemove, onCompare }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-bg-subtle/50 border border-white/5 rounded-2xl p-4 group"
      data-testid={`crew-member-${member.crew_user_id}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/10 flex-shrink-0">
          {member.crew_picture ? (
            <img src={member.crew_picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center text-xs font-bold">
              {(member.crew_name || '?')[0].toUpperCase()}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{member.crew_name || 'Unknown'}</p>
          <p className="text-[10px] text-muted-foreground">{member.surf_level || 'beginner'}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            data-testid={`compare-crew-${member.crew_user_id}`}
            onClick={() => onCompare(member.crew_user_id)}
            className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
            title="Compare stats"
          >
            <BarChart3 className="w-4 h-4 text-primary" />
          </button>
          <button
            data-testid={`remove-crew-${member.crew_user_id}`}
            onClick={onRemove}
            className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-full transition-all"
          >
            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>
      <div className="flex gap-4 mt-2.5 pl-[52px] text-[11px] text-muted-foreground">
        <span>{member.total_sessions || 0} sessions</span>
        <span>{Math.round((member.total_minutes || 0) / 60)}h water time</span>
      </div>
    </motion.div>
  );
}

function StatsCompare({ comparison, onClose }) {
  if (!comparison) return null;
  const { you, crew_member } = comparison;
  const stats = [
    { label: 'Sessions', a: you.total_sessions, b: crew_member.total_sessions },
    { label: 'Hours', a: Math.round(you.total_minutes / 60), b: Math.round(crew_member.total_minutes / 60) },
    { label: 'Level', a: you.surf_level, b: crew_member.surf_level, text: true },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      className="mb-5 bg-bg-subtle/80 border border-primary/20 rounded-2xl p-4"
      data-testid="stats-comparison"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-primary">Stats Comparison</p>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full">
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        <p className="text-[10px] text-muted-foreground truncate">{you.name || 'You'}</p>
        <p className="text-[10px] text-muted-foreground"></p>
        <p className="text-[10px] text-muted-foreground truncate">{crew_member.name}</p>
      </div>
      {stats.map(s => (
        <div key={s.label} className="grid grid-cols-3 gap-2 text-center py-1.5 border-t border-white/5">
          <p className={`text-sm font-bold ${!s.text && s.a > s.b ? 'text-primary' : ''}`}>{s.a}</p>
          <p className="text-[10px] text-muted-foreground self-center">{s.label}</p>
          <p className={`text-sm font-bold ${!s.text && s.b > s.a ? 'text-primary' : ''}`}>{s.b}</p>
        </div>
      ))}
    </motion.div>
  );
}

function RemoveConfirmModal({ member, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('');
  const memberName = member?.crew_name || '';
  const memberUsername = member?.crew_username || '';
  const displayName = memberUsername || memberName;
  const matchTarget = memberUsername || memberName.toLowerCase().replace(/\s+/g, '');
  const isMatch = typed.trim().toLowerCase() === matchTarget.toLowerCase();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl p-5 shadow-2xl"
        data-testid="remove-crew-modal"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="w-8 h-8 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <p className="text-sm font-semibold">Remove from crew?</p>
            <p className="text-[11px] text-muted-foreground">This can't be undone</p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          To confirm, type <span className="text-red-400 font-mono font-medium">{displayName}</span> below:
        </p>

        <input
          data-testid="remove-crew-confirm-input"
          type="text"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder={displayName}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-xs outline-none focus:border-red-400/50 placeholder:text-muted-foreground/25 mb-3"
          autoFocus
        />

        <div className="flex gap-2">
          <button
            data-testid="remove-crew-cancel-btn"
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-xs font-medium bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            data-testid="remove-crew-confirm-btn"
            onClick={onConfirm}
            disabled={!isMatch}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Remove
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CrewRipple() {
  const [network, setNetwork] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});

  const loadNetwork = useCallback(async () => {
    try {
      const [netRes, breakRes] = await Promise.all([
        authFetch(`${API}/api/crew/network`),
        authFetch(`${API}/api/crew/aura-breakdown`),
      ]);
      if (netRes.ok) setNetwork(await netRes.json());
      if (breakRes.ok) setBreakdown(await breakRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadNetwork(); }, [loadNetwork]);

  if (loading) return null;
  if (!network) return null;

  const { direct_invites, direct_count, total_network_size, total_chain_aura, invited_by, reward_levels } = network;
  const hasNetwork = direct_count > 0 || invited_by;

  return (
    <div className="mb-5" data-testid="crew-ripple">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Flame className="w-3.5 h-3.5 text-amber-400" />
          <p className="text-xs tracking-widest uppercase text-muted-foreground">Stoke Ripple</p>
        </div>
        {total_chain_aura > 0 && (
          <span className="text-[10px] bg-amber-500/15 text-amber-400 px-2 py-0.5 rounded-full font-bold" data-testid="ripple-aura-total">
            +{total_chain_aura} aura earned
          </span>
        )}
      </div>

      {/* How it works — plain English */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl p-3 mb-3">
        <p className="text-[9px] text-muted-foreground/50 font-medium mb-2 uppercase tracking-wider">How it works</p>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-amber-500/15 flex items-center justify-center flex-shrink-0"><span className="text-[8px] font-bold text-amber-400">1</span></div>
            <p className="text-[10px] text-muted-foreground/70">You invite a friend <span className="font-bold text-amber-400">+{reward_levels?.[1] || 50} aura</span></p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0"><span className="text-[8px] font-bold text-orange-400">2</span></div>
            <p className="text-[10px] text-muted-foreground/70">They invite a friend <span className="font-bold text-orange-400">+{reward_levels?.[2] || 25} aura for you</span></p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-rose-500/15 flex items-center justify-center flex-shrink-0"><span className="text-[8px] font-bold text-rose-400">3+</span></div>
            <p className="text-[10px] text-muted-foreground/70">The stoke keeps spreading <span className="text-muted-foreground/40">({reward_levels?.[3] || 10}, {reward_levels?.[4] || 5}, {reward_levels?.[5] || 2} aura)</span></p>
          </div>
        </div>
      </div>

      {/* Invited by */}
      {invited_by && (
        <div className="bg-primary/[0.04] border border-primary/10 rounded-xl p-3 mb-3 flex items-center gap-2.5" data-testid="invited-by">
          <div className="w-7 h-7 rounded-full overflow-hidden border border-white/10 flex-shrink-0">
            {invited_by.picture ? <img src={invited_by.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted flex items-center justify-center text-[9px] font-bold">{(invited_by.name || '?')[0]}</div>}
          </div>
          <div>
            <p className="text-[10px] text-muted-foreground/50">Brought in by</p>
            <p className="text-xs font-medium">{invited_by.name}{invited_by.username ? ` @${invited_by.username}` : ''}</p>
          </div>
        </div>
      )}

      {/* Network stats */}
      {hasNetwork && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-amber-400" data-testid="direct-invites-count">{direct_count}</p>
            <p className="text-[8px] text-muted-foreground/40">Invited</p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-orange-400" data-testid="network-size">{total_network_size}</p>
            <p className="text-[8px] text-muted-foreground/40">Ripple reach</p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-rose-400" data-testid="ripple-aura">{total_chain_aura}</p>
            <p className="text-[8px] text-muted-foreground/40">Aura earned</p>
          </div>
        </div>
      )}

      {/* Referral tree */}
      {direct_invites.length > 0 && (
        <div className="space-y-1" data-testid="referral-tree">
          {direct_invites.map((node, idx) => {
            const hasChildren = node.direct_invites > 0;
            const isExpanded = expandedNodes[node.user_id];
            return (
              <div key={`${node.user_id}-${idx}`}>
                <div
                  className="flex items-center gap-2.5 bg-white/[0.02] border border-white/5 rounded-xl p-2.5 hover:border-white/10 transition-colors"
                  data-testid={`ripple-node-${node.user_id}`}
                >
                  {hasChildren ? (
                    <button onClick={() => setExpandedNodes(prev => ({...prev, [node.user_id]: !prev[node.user_id]}))} className="p-0.5">
                      {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground/30" /> : <ChevronRight className="w-3 h-3 text-muted-foreground/30" />}
                    </button>
                  ) : (
                    <div className="w-4 flex justify-center"><div className="w-1 h-1 rounded-full bg-amber-400/30" /></div>
                  )}
                  <div className="w-7 h-7 rounded-full overflow-hidden border border-amber-400/20 flex-shrink-0">
                    {node.picture ? <img src={node.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-amber-500/10 flex items-center justify-center text-[9px] font-bold text-amber-400">{(node.name || '?')[0]}</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{node.name}{node.username ? <span className="text-muted-foreground/40 font-normal"> @{node.username}</span> : ''}</p>
                    <div className="flex items-center gap-2 text-[9px] text-muted-foreground/40">
                      {node.direct_invites > 0 && <span className="text-orange-400/60">{node.direct_invites} invite{node.direct_invites !== 1 ? 's' : ''}</span>}
                      {node.total_downstream > 0 && <span>{node.total_downstream} rippled</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Zap className="w-2.5 h-2.5 text-amber-400/50" />
                    <span className="text-[10px] font-bold text-amber-400">+{reward_levels?.[1] || 50}</span>
                  </div>
                </div>
                {isExpanded && node.total_downstream > 0 && (
                  <div className="ml-6 pl-4 border-l border-amber-400/10 py-1">
                    <p className="text-[9px] text-muted-foreground/30 italic">
                      {node.direct_invites} invite{node.direct_invites !== 1 ? 's' : ''} spreading the stoke — {node.total_downstream} surfer{node.total_downstream !== 1 ? 's' : ''} downstream
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {!hasNetwork && (
        <div className="text-center py-4 bg-white/[0.02] border border-white/5 rounded-xl">
          <Users className="w-5 h-5 text-muted-foreground/20 mx-auto mb-1.5" />
          <p className="text-[10px] text-muted-foreground/30">Invite crew to start spreading the stoke</p>
          <p className="text-[8px] text-muted-foreground/20 mt-0.5">Earn aura as your crew grows</p>
        </div>
      )}

      {/* History toggle */}
      {breakdown && breakdown.events?.length > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-[9px] text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors inline-flex items-center gap-1"
            data-testid="ripple-history-toggle"
          >
            {showHistory ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Aura log ({breakdown.events.length})
          </button>
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <div className="mt-2 space-y-1 max-h-[200px] overflow-y-auto" data-testid="ripple-history-list">
                  {breakdown.events.map((e, i) => {
                    const levelColors = { 1: 'text-amber-400 bg-amber-500/10', 2: 'text-orange-400 bg-orange-500/10', 3: 'text-rose-400 bg-rose-500/10', 4: 'text-pink-400 bg-pink-500/10', 5: 'text-purple-400 bg-purple-500/10' };
                    const cls = levelColors[e.level] || levelColors[1];
                    return (
                      <div key={i} className="flex items-center gap-2 py-1.5 px-2 bg-white/[0.015] rounded-lg text-[10px]">
                        <span className={`font-bold px-1.5 py-0.5 rounded ${cls}`}>+{e.amount}</span>
                        <div className="w-4 h-4 rounded-full overflow-hidden flex-shrink-0">
                          {e.from_picture ? <img src={e.from_picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted flex items-center justify-center text-[7px]">{(e.from_name || '?')[0]}</div>}
                        </div>
                        <span className="text-muted-foreground/60 truncate flex-1">{e.from_name} joined</span>
                        {e.created_at && <span className="text-muted-foreground/25 flex-shrink-0">{new Date(e.created_at).toLocaleDateString()}</span>}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

export default function Crew({ user }) {
  const [crew, setCrew] = useState([]);
  const [invites, setInvites] = useState({ received: [], sent: [] });
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingMember, setRemovingMember] = useState(null);

  const loadAll = async () => {
    try {
      const [crewRes, invRes] = await Promise.all([
        authFetch(`${API}/api/crew`),
        authFetch(`${API}/api/crew/invites`),
      ]);
      if (crewRes.ok) setCrew((await crewRes.json()).crew || []);
      if (invRes.ok) setInvites(await invRes.json());
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleCompare = async (crewUserId) => {
    try {
      const res = await authFetch(`${API}/api/crew/${crewUserId}/stats`);
      if (res.ok) setComparison(await res.json());
    } catch {}
  };

  const handleRemoveClick = (member) => {
    setRemovingMember(member);
  };

  const handleRemoveConfirm = async () => {
    if (!removingMember) return;
    await authFetch(`${API}/api/crew/${removingMember.crew_user_id}`, { method: 'DELETE',  });
    setRemovingMember(null);
    loadAll();
    setComparison(null);
  };

  if (loading) {
    return (
      <PageShell title="Crew">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  const { isPro } = useSubscription();

  return (
    <PageShell title="Crew">
      {!isPro ? (
        <div className="p-5">
          <InviteForm onInviteSent={loadAll} user={user} />
          <div className="mt-4">
            <ProGate feature="crew" />
          </div>
        </div>
      ) : (
      <>
      <div className="p-5" data-testid="crew-page">
        <InviteForm onInviteSent={loadAll} user={user} />
        <PendingInvites invites={invites} onAction={loadAll} />
        <CrewRipple />

        <AnimatePresence>
          {comparison && <StatsCompare comparison={comparison} onClose={() => setComparison(null)} />}
        </AnimatePresence>

        <p className="text-xs tracking-widest uppercase text-muted-foreground mb-3">
          Your Crew {crew.length > 0 && `(${crew.length})`}
        </p>

        {crew.length > 0 ? (
          <div className="space-y-2">
            {crew.map((m) => (
              <CrewMemberCard
                key={m.crew_user_id}
                member={m}
                onRemove={() => handleRemoveClick(m)}
                onCompare={handleCompare}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Users className="w-6 h-6 text-muted-foreground/50 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">no crew members yet</p>
            <p className="text-[10px] text-muted-foreground/60 mt-1">invite friends by username or share a link</p>
          </div>
        )}
      </div>

      {/* Remove Confirmation Modal */}
      <AnimatePresence>
        {removingMember && (
          <RemoveConfirmModal
            member={removingMember}
            onConfirm={handleRemoveConfirm}
            onCancel={() => setRemovingMember(null)}
          />
        )}
      </AnimatePresence>
      </>
      )}
    </PageShell>
  );
}
